/** Quarter-hour shoot scheduling helpers for studio booking dates. */

export const HALF_HOUR_TIMES: string[] = (() => {
  const times: string[] = [];
  for (let hour = 5; hour <= 21; hour += 1) {
    for (const minute of [0, 15, 30, 45]) {
      if (hour === 21 && minute > 0) continue;
      times.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return times;
})();

export const DURATION_OPTIONS_MINUTES = [
  30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 420, 480,
];

export function parseClock(value?: string): number | null {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value || '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function formatClock(totalMinutes: number): string {
  const wrapped = ((Math.round(totalMinutes) % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function roundToHalfHour(value?: string): string {
  const total = parseClock(value);
  if (total == null) return '';
  return formatClock(Math.round(total / 30) * 30);
}

export function addMinutesToClock(start?: string, durationMinutes?: number): string {
  const total = parseClock(start);
  if (total == null || !Number.isFinite(Number(durationMinutes))) return '';
  return formatClock(total + Number(durationMinutes));
}

export function durationBetween(start?: string, end?: string): number | null {
  const from = parseClock(start);
  const to = parseClock(end);
  if (from == null || to == null) return null;
  let span = to - from;
  if (span <= 0) span += 24 * 60;
  return span;
}

export function formatDurationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  if (Number.isInteger(hours)) return hours === 1 ? '1 hour' : `${hours} hours`;
  const whole = Math.floor(hours);
  const rem = minutes % 60;
  if (whole === 0) return `${rem} min`;
  return `${whole}h ${rem}m`;
}

export function ensureDurationOption(minutes: number | null | undefined): number[] {
  const base = DURATION_OPTIONS_MINUTES.slice();
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return base;
  const rounded = Math.max(30, Math.round(value / 30) * 30);
  if (!base.includes(rounded)) {
    base.push(rounded);
    base.sort((left, right) => left - right);
  }
  return base;
}

/**
 * Start time for the confirmation form.
 * A time already saved on the booking wins. Otherwise the time already suggested
 * on the quotation is used, and it can still be changed before confirming.
 */
export function confirmationStartTime(
  saved?: { proposedStartTime?: string; startTime?: string } | null,
  suggestedTime?: string,
): string {
  const savedText = String((saved && (saved.proposedStartTime || saved.startTime)) || '').trim();
  const source = savedText || String(suggestedTime || '').trim();
  const exact = source.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(exact)) return exact;
  return roundToHalfHour(source) || '';
}

export function ensureTimeOption(value?: string): string[] {
  const times = HALF_HOUR_TIMES.slice();
  const rounded = roundToHalfHour(value);
  if (rounded && !times.includes(rounded)) {
    times.push(rounded);
    times.sort();
  }
  const exact = String(value || '').trim().slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(exact) && !times.includes(exact)) {
    times.push(exact);
    times.sort();
  }
  return times;
}
